import { FormikProvider, useFormik } from 'formik';
import { KeycloakRole, UpdateUserByComplianceAdminRequest } from 'identity-center-api';
import toast from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import * as yup from 'yup';
import { COMPLIANCE_CURRENT_USER_QUERY_KEY } from '@/modules/auth/queries/user.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { useUpdateUserByComplianceAdminMutation } from '@/modules/admin-dashboard/queries/users.ts';
import { BaseModalProps, Button, ModalContentWrapper, TextInputField } from 'ui';
import { GetCurrentUserResponse, UserResponse, UserRole } from 'compliance-api';

type ComplianceRole = KeycloakRole.ComplianceAdmin | KeycloakRole.ComplianceUser;

const roleOptions: { value: ComplianceRole; label: string }[] = [
  { value: KeycloakRole.ComplianceUser, label: 'User' },
  { value: KeycloakRole.ComplianceAdmin, label: 'Admin' },
];

const toComplianceUserRole = (role: ComplianceRole) =>
  role === KeycloakRole.ComplianceAdmin ? UserRole.Admin : UserRole.User;

type EditUserFormValues = Pick<UpdateUserByComplianceAdminRequest, 'firstName' | 'lastName'> & {
  email: string;
  roles: ComplianceRole[];
};

const validationSchema = yup.object<EditUserFormValues>({
  firstName: yup
    .string()
    .trim()
    .min(3, 'Must be at least 3 characters')
    .max(255, 'Must be at most 255 characters')
    .required('This field is required'),
  lastName: yup
    .string()
    .trim()
    .min(3, 'Must be at least 3 characters')
    .max(255, 'Must be at most 255 characters')
    .required('This field is required'),
  email: yup.string().required(),
  roles: yup.array().of(yup.mixed<ComplianceRole>()).required('This field is required'),
});

interface EditUserModalProps extends BaseModalProps {
  user: UserResponse;
  organizationId: string;
  businessId: number;
  isCurrentUser: boolean;
}

const EditUserModal = ({
  open,
  onClose,
  user,
  organizationId,
  businessId,
  isCurrentUser,
}: EditUserModalProps) => {
  const auth = useAuth();
  const initialRoles = roleOptions
    .filter(
      option =>
        (option.value === KeycloakRole.ComplianceUser && user.roles?.includes(UserRole.User)) ||
        (option.value === KeycloakRole.ComplianceAdmin && user.roles?.includes(UserRole.Admin)),
    )
    .map(option => option.value);
  const { mutateAsync: updateUser, isLoading } = useUpdateUserByComplianceAdminMutation(businessId);

  const formik = useFormik<EditUserFormValues>({
    initialValues: {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      roles: initialRoles,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      if (!user.keycloak_user_id) return;

      const firstName = values.firstName.trim();
      const lastName = values.lastName.trim();
      const profileChanged = firstName !== user.first_name || lastName !== user.last_name;
      const rolesToAdd = values.roles
        .filter(role => !initialRoles.includes(role))
        .map(role => ({
          user_id: user.keycloak_user_id as string,
          organization_id: organizationId,
          role,
        }));
      const rolesToRemove = initialRoles
        .filter(role => !values.roles.includes(role))
        .map(role => ({
          user_id: user.keycloak_user_id as string,
          organization_id: organizationId,
          role,
        }));
      const rolesChanged = rolesToAdd.length > 0 || rolesToRemove.length > 0;

      await updateUser({
        userId: user.keycloak_user_id,
        profile: profileChanged
          ? {
              organization_id: organizationId,
              firstName,
              lastName,
            }
          : undefined,
        rolesToAdd,
        rolesToRemove,
      });

      if (isCurrentUser && (profileChanged || rolesChanged)) {
        queryClient.setQueryData<GetCurrentUserResponse>(
          [COMPLIANCE_CURRENT_USER_QUERY_KEY],
          currentUser =>
            currentUser
              ? {
                  ...currentUser,
                  user: {
                    ...currentUser.user,
                    first_name: firstName,
                    last_name: lastName,
                    roles: Array.from(
                      new Set([
                        ...(currentUser.user.roles ?? []),
                        ...rolesToAdd.map(role => toComplianceUserRole(role.role)),
                      ]),
                    ).filter(
                      role =>
                        !rolesToRemove.some(
                          removedRole => role === toComplianceUserRole(removedRole.role),
                        ),
                    ),
                  },
                }
              : currentUser,
        );

        try {
          await auth.signinSilent();
        } catch {
          toast.error(
            'Changes were saved, but the session could not be refreshed. Please sign in again.',
          );
        }
      }
      onClose();
    },
  });

  return (
    <ModalContentWrapper open={open} title="Edit user" onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col gap-8" onSubmit={formik.handleSubmit}>
          <div className="w-full flex gap-x-2 gap-y-8 flex-wrap lg:flex-nowrap align-middle justify-between">
            <TextInputField name="firstName" label="First name" disabled={isLoading} />
            <TextInputField name="lastName" label="Last name" disabled={isLoading} />
          </div>
          <TextInputField name="email" label="Email" disabled />
          <div>
            <label className="flex text-[10px] leading-[18px] font-[Inter] text-gray-900 text-opacity-[.64] uppercase font-medium">
              Role
            </label>
            <div className="flex items-center bg-gray-100 rounded min-h-[36px] divide-x divide-gray-200">
              {roleOptions.map(option => {
                const isSelected = formik.values.roles.includes(option.value);

                return (
                  <div key={option.value} className="w-full flex items-center justify-center">
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() =>
                        formik.setFieldValue(
                          'roles',
                          isSelected
                            ? formik.values.roles.filter(role => role !== option.value)
                            : [...formik.values.roles, option.value],
                        )
                      }
                      className={`mx-[2px] w-full min-h-[32px] rounded-sm font-[Inter] text-[14px] leading-[20px] transition-all duration-300 ${
                        isSelected
                          ? 'bg-white border border-blue-700 text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {option.label}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <Button type="submit" loading={isLoading} disabled={isLoading}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default EditUserModal;
